import { Box, Typography } from '@mui/material';

import { LaunchpadSubtask as LaunchpadSubtaskType, TaskStatus } from './LaunchpadProgress.interface';

interface LaunchpadSubtaskProps {
    subtask: LaunchpadSubtaskType;
    subtaskIndex: number;
    totalSubtasks: number;
}

interface SubtaskBulletProps {
    status: TaskStatus;
}

function CompletedBullet() {
    return (
        <Box
            sx={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: 'success.dark',
            }}
        />
    );
}

function InProgressBullet() {
    return (
        <Box
            sx={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: 'primary.main',
                position: 'relative',
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                    animation: 'radar 1.5s ease-out infinite',
                },
                '@keyframes radar': {
                    '0%': {
                        transform: 'scale(1)',
                        opacity: 0.6,
                    },
                    '100%': {
                        transform: 'scale(3)',
                        opacity: 0,
                    },
                },
            }}
        />
    );
}

function PendingBullet() {
    return (
        <Box
            sx={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: 'grey.200',
            }}
        />
    );
}

function DefaultBullet() {
    return (
        <Box
            sx={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: 'grey.400',
            }}
        />
    );
}

function SubtaskBullet({ status }: SubtaskBulletProps) {
    if (status === TaskStatus.COMPLETED) {
        return <CompletedBullet />;
    }

    if (status === TaskStatus.IN_PROGRESS) {
        return <InProgressBullet />;
    }

    if (status === TaskStatus.PENDING || status === TaskStatus.READY) {
        return <PendingBullet />;
    }

    return <DefaultBullet />;
}

function LaunchpadSubtaskComponent({ subtask, subtaskIndex, totalSubtasks }: LaunchpadSubtaskProps) {
    const isFirstSubtask = subtaskIndex === 0;
    const isLastSubtask = subtaskIndex === totalSubtasks - 1;
    const hasDescription = Boolean(subtask.description);

    return (
        <Box sx={{ display: 'flex', gap: 1 }}>
            {/* Indicator column: line + bullet centered with content */}
            <Box
                sx={{
                    width: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}>
                {/* Top line segment */}
                <Box
                    data-testid="connecting-line-top"
                    sx={{
                        flex: 1,
                        minHeight: '12px',
                        width: '1px',
                        backgroundColor: isFirstSubtask ? 'transparent' : 'grey.200',
                    }}
                />
                {/* Bullet centered with content */}
                <Box sx={{ my: 1 }}>
                    <SubtaskBullet status={subtask.status} />
                </Box>
                {/* Bottom line segment */}
                <Box
                    data-testid="connecting-line-bottom"
                    sx={{
                        flex: 1,
                        minHeight: '12px',
                        width: '1px',
                        backgroundColor: isLastSubtask ? 'transparent' : 'grey.200',
                    }}
                />
            </Box>
            {/* Content */}
            <Box sx={{ flex: 1, py: 1.5 }}>
                <Typography variant="body1">{subtask.name}</Typography>
                {hasDescription && (
                    <Typography variant="body1" color="text.secondary">
                        {subtask.description}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

export default LaunchpadSubtaskComponent;
